import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SUPPLIER_ORDER_STAGES } from './supplierOrderDraftStage';

interface SupplierOrderStageRailProps {
  /** Index of the stage the ZD is in (0–4). */
  currentIndex: number;
  /** Detailed status under the current stage when the stage groups several (makieta `.sub`). */
  subStatusLabel?: string | null;
  /** Finished stages become buttons that return to them (the ZD card); absent = plain rail. */
  onReturnToStage?: (stageIndex: number) => void;
}

/**
 * The five stages of a ZD (makieta `renderRail`): done = filled with a tick,
 * the current one = blue outline, later = grey. Same rule as the ZK rail.
 */
export default function SupplierOrderStageRail({ currentIndex, subStatusLabel, onReturnToStage }: SupplierOrderStageRailProps) {
  const { t } = useTranslation(['orders']);
  return (
    <div className="zd-stage__rail-wrap">
      <ol className="zd-stage__rail">
        {SUPPLIER_ORDER_STAGES.map((stage, index) => {
          const state = index < currentIndex ? 'is-done' : index === currentIndex ? 'is-current' : '';
          return (
            <li
              key={stage.key}
              className={`zd-stage__rail-item ${state}`.trim()}
              aria-current={index === currentIndex ? 'step' : undefined}
              data-testid={`supplier-order-stage-${stage.key}`}
            >
              {index < currentIndex && onReturnToStage ? (
                <button
                  type="button"
                  className="zd-stage__rail-btn"
                  onClick={() => onReturnToStage(index)}
                  title={t('orders:supplierOrders.card.returnTo', { stage: t(`orders:supplierOrders.card.stages.${stage.key}`) })}
                  data-testid={`supplier-order-stage-return-${stage.key}`}
                >
                  <span className="zd-stage__dot" aria-hidden="true"><CheckOutlined /></span>
                  <span className="zd-stage__rail-label">{t(`orders:supplierOrders.card.stages.${stage.key}`)}</span>
                </button>
              ) : (
                <>
                  <span className="zd-stage__dot" aria-hidden="true">{index < currentIndex ? <CheckOutlined /> : index + 1}</span>
                  <span className="zd-stage__rail-label">{t(`orders:supplierOrders.card.stages.${stage.key}`)}</span>
                </>
              )}
              {index === currentIndex && subStatusLabel && stage.statuses.length > 1 ? (
                <span className="zd-stage__rail-sub" data-testid="supplier-order-stage-substatus">{subStatusLabel}</span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
